import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  BookOpen, Search, Globe, ChevronRight, Clock, Edit2, Trash2,
  GraduationCap, Filter, X, ChevronDown, ChevronUp, Plus, Save,
  AlertCircle, Loader2, FileText, List, Eye, ArrowLeft
} from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const AdminGrammarEditor = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isAdmin = user?.is_admin;

  // Topic list state
  const [topics, setTopics] = useState([]);
  const [filteredTopics, setFilteredTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('all');
  const [selectedLevel, setSelectedLevel] = useState('all');

  // Editor state
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingTopic, setEditingTopic] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    language: 'es',
    level: 'beginner',
    cefr_level: 'A1',
    study_time_minutes: 15,
    objectives: '',
    content: '',
    exercises: [],
    prerequisites: [],
    tags: [],
    is_active: true,
  });

  // Exercise editor state
  const [currentExercise, setCurrentExercise] = useState({
    question: '',
    answer: '',
    type: 'fill-blank'
  });

  const languages = [
    { code: 'en', name: 'English', flag: 'gb' },
    { code: 'es', name: 'Spanish', flag: 'es' },
    { code: 'fr', name: 'French', flag: 'fr' },
    { code: 'de', name: 'German', flag: 'de' },
    { code: 'it', name: 'Italian', flag: 'it' },
    { code: 'pt', name: 'Portuguese', flag: 'pt' },
    { code: 'ru', name: 'Russian', flag: 'ru' },
    { code: 'zh', name: 'Chinese', flag: 'cn' },
    { code: 'ja', name: 'Japanese', flag: 'jp' },
    { code: 'ko', name: 'Korean', flag: 'kr' },
    { code: 'ar', name: 'Arabic', flag: 'sa' },
    { code: 'fa', name: 'Persian', flag: 'ir' },
    { code: 'sw', name: 'Swahili', flag: 'ke' },
    { code: 'hbo', name: 'Biblical Hebrew', flag: 'il' },
    { code: 'grc', name: 'Biblical Greek', flag: 'gr' },
  ];

  const levels = [
    { value: 'beginner', label: 'Beginner', cefr: 'A1' },
    { value: 'elementary', label: 'Elementary', cefr: 'A2' },
    { value: 'intermediate', label: 'Intermediate', cefr: 'B1' },
    { value: 'upper_intermediate', label: 'Upper Intermediate', cefr: 'B2' },
    { value: 'advanced', label: 'Advanced', cefr: 'C1' },
    { value: 'proficient', label: 'Proficient', cefr: 'C2' },
  ];

  const exerciseTypes = [
    { value: 'fill-blank', label: 'Fill in the Blank' },
    { value: 'multiple-choice', label: 'Multiple Choice' },
    { value: 'translation', label: 'Translation' },
    { value: 'conjugation', label: 'Conjugation' },
    { value: 'sentence-building', label: 'Sentence Building' },
  ];

  // Redirect non-admins (wait for auth to load first)
  useEffect(() => {
    // Only check once user is loaded (not undefined/null)
    if (user !== undefined && user !== null && !isAdmin) {
      navigate('/dashboard');
      toast.error('Admin access required');
    }
  }, [user, isAdmin, navigate]);

  // Fetch topics
  useEffect(() => {
    fetchTopics();
  }, []);

  // Filter topics
  useEffect(() => {
    let filtered = [...topics];
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(t => 
        t.title?.toLowerCase().includes(query) ||
        t.content?.toLowerCase().includes(query) ||
        t.objectives?.toLowerCase().includes(query)
      );
    }
    
    if (selectedLanguage !== 'all') {
      filtered = filtered.filter(t => t.language === selectedLanguage);
    }
    
    if (selectedLevel !== 'all') {
      filtered = filtered.filter(t => t.level === selectedLevel);
    }
    
    setFilteredTopics(filtered);
  }, [topics, searchQuery, selectedLanguage, selectedLevel]);

  const fetchTopics = async () => {
    try {
      setLoading(true);
      // Fetch multiple pages if needed (API limit is 100)
      let allTopics = [];
      let page = 1;
      let hasMore = true;
      
      while (hasMore) {
        const response = await api.get(`/grammar-library/topics?limit=100&page=${page}`);
        const data = response.data;
        allTopics = [...allTopics, ...(data.topics || [])];
        
        if (page >= data.total_pages || data.topics?.length < 100) {
          hasMore = false;
        } else {
          page++;
        }
      }
      
      setTopics(allTopics);
    } catch (err) {
      console.error('Failed to fetch topics:', err);
      toast.error('Failed to load grammar topics');
    } finally {
      setLoading(false);
    }
  };

  const openNewTopic = () => {
    setEditingTopic(null);
    setFormData({
      title: '',
      language: 'es',
      level: 'beginner',
      cefr_level: 'A1',
      study_time_minutes: 15,
      objectives: '',
      content: '',
      exercises: [],
      prerequisites: [],
      tags: [],
      is_active: true,
    });
    setIsEditorOpen(true);
  };

  const openEditTopic = async (topicId) => {
    try {
      const response = await api.get(`/grammar-library/topic/${topicId}`);
      const topic = response.data;
      
      setEditingTopic(topic);
      setFormData({
        title: topic.title || '',
        language: topic.language || 'es',
        level: topic.level || 'beginner',
        cefr_level: topic.cefr_level || 'A1',
        study_time_minutes: topic.study_time_minutes || 15,
        objectives: topic.objectives || '',
        content: topic.content || '',
        exercises: topic.exercises || [],
        prerequisites: topic.prerequisites || [],
        tags: topic.tags || [],
        is_active: topic.is_active !== false,
      });
      setIsEditorOpen(true);
    } catch (err) {
      console.error('Failed to load topic:', err);
      toast.error('Failed to load topic details');
    }
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!formData.content.trim()) {
      toast.error('Content is required');
      return;
    }

    try {
      setSaving(true);
      
      const payload = {
        ...formData,
        updated_at: new Date().toISOString(),
      };

      if (editingTopic) {
        // Update existing
        await api.put(`/grammar-library/${editingTopic.id}`, payload);
        toast.success('Topic updated successfully');
      } else {
        // Create new
        payload.created_at = new Date().toISOString();
        await api.post('/grammar-library', payload);
        toast.success('Topic created successfully');
      }

      setIsEditorOpen(false);
      fetchTopics();
    } catch (err) {
      console.error('Failed to save topic:', err);
      toast.error(err.response?.data?.detail || 'Failed to save topic');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (topicId) => {
    try {
      setDeleting(true);
      await api.delete(`/grammar-library/${topicId}`);
      toast.success('Topic deleted successfully');
      fetchTopics();
    } catch (err) {
      console.error('Failed to delete topic:', err);
      toast.error('Failed to delete topic');
    } finally {
      setDeleting(false);
    }
  };

  const addExercise = () => {
    if (!currentExercise.question.trim()) {
      toast.error('Exercise question is required');
      return;
    }
    setFormData(prev => ({
      ...prev,
      exercises: [...prev.exercises, { ...currentExercise }]
    }));
    setCurrentExercise({ question: '', answer: '', type: 'fill-blank' });
  };

  const removeExercise = (index) => {
    setFormData(prev => ({
      ...prev,
      exercises: prev.exercises.filter((_, i) => i !== index)
    }));
  };

  const getLevelColor = (level) => {
    const colors = {
      beginner: 'bg-green-500/20 text-green-400',
      elementary: 'bg-blue-500/20 text-blue-400',
      intermediate: 'bg-yellow-500/20 text-yellow-400',
      upper_intermediate: 'bg-orange-500/20 text-orange-400',
      advanced: 'bg-red-500/20 text-red-400',
      proficient: 'bg-purple-500/20 text-purple-400',
    };
    return colors[level] || 'bg-gray-500/20 text-gray-400';
  };

  const getLanguageFlag = (code) => {
    const lang = languages.find(l => l.code === code);
    return lang?.flag || 'un';
  };

  // Pull the language code off a topic regardless of which field it's stored
  // in — historical docs used ``language`` (2-letter code), while the current
  // schema uses ``language_code``. Without this fallback every lesson
  // rendered the UN flag.
  const getTopicLangCode = (topic) =>
    topic?.language_code || topic?.language || topic?.lang || '';

  if (!user) {
    // Still loading auth
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-purple-400" />
      </div>
    );
  }
  
  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-background p-4 md:p-6 overflow-x-hidden">
      <div className="max-w-7xl mx-auto space-y-6 overflow-hidden">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <GraduationCap className="h-6 w-6 text-purple-400" />
                Grammar Editor
              </h1>
              <p className="text-muted-foreground text-sm">
                Create and edit grammar lessons
              </p>
            </div>
          </div>
          <Button onClick={openNewTopic} className="gap-2">
            <Plus className="h-4 w-4" />
            New Topic
          </Button>
        </div>

        {/* Filters */}
        <Card className="bg-card/50">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search topics..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
                <SelectTrigger className="w-full md:w-48">
                  <Globe className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="All Languages" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Languages</SelectItem>
                  {languages.map(lang => (
                    <SelectItem key={lang.code} value={lang.code}>
                      <span className="flex items-center gap-2">
                        <img 
                          src={`https://flagcdn.com/w20/${lang.flag}.png`} 
                          alt="" 
                          className="w-4 h-3 rounded-sm"
                        />
                        {lang.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={selectedLevel} onValueChange={setSelectedLevel}>
                <SelectTrigger className="w-full md:w-48">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="All Levels" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Levels</SelectItem>
                  {levels.map(level => (
                    <SelectItem key={level.value} value={level.value}>
                      {level.label} ({level.cefr})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-purple-500/10 border-purple-500/20">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-purple-400">{topics.length}</p>
              <p className="text-xs text-muted-foreground">Total Topics</p>
            </CardContent>
          </Card>
          <Card className="bg-blue-500/10 border-blue-500/20">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-400">
                {[...new Set(topics.map(t => t.language))].length}
              </p>
              <p className="text-xs text-muted-foreground">Languages</p>
            </CardContent>
          </Card>
          <Card className="bg-green-500/10 border-green-500/20">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-400">
                {topics.filter(t => t.exercises?.length > 0).length}
              </p>
              <p className="text-xs text-muted-foreground">With Exercises</p>
            </CardContent>
          </Card>
          <Card className="bg-amber-500/10 border-amber-500/20">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-amber-400">
                {filteredTopics.length}
              </p>
              <p className="text-xs text-muted-foreground">Showing</p>
            </CardContent>
          </Card>
        </div>

        {/* Topic List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-purple-400" />
          </div>
        ) : filteredTopics.length === 0 ? (
          <Card className="bg-card/30">
            <CardContent className="p-12 text-center">
              <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No grammar topics found</p>
              <Button onClick={openNewTopic} className="mt-4 gap-2">
                <Plus className="h-4 w-4" />
                Create First Topic
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3">
            {filteredTopics.map((topic) => (
              <Card 
                key={topic.id || topic._key} 
                className="bg-card/50 hover:bg-card/70 transition-colors"
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <img
                        src={`https://flagcdn.com/w40/${getLanguageFlag(getTopicLangCode(topic))}.png`}
                        alt={topic.language}
                        className="w-8 h-6 rounded object-cover flex-shrink-0 mt-1"
                      />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-white truncate">{topic.title}</h3>
                        <div className="flex flex-wrap items-center gap-2 mt-1">
                          <Badge className={getLevelColor(topic.level)}>
                            {topic.cefr_level || topic.level}
                          </Badge>
                          {topic.study_time_minutes && (
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {topic.study_time_minutes}m
                            </span>
                          )}
                          {topic.exercises?.length > 0 && (
                            <span className="text-xs text-purple-400">
                              {topic.exercises.length} exercises
                            </span>
                          )}
                        </div>
                        {topic.objectives && (
                          <p className="text-xs text-muted-foreground mt-1 truncate">
                            {topic.objectives}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => openEditTopic(topic.id)}
                        className="gap-1"
                      >
                        <Edit2 className="h-4 w-4" />
                        Edit
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Topic</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete "{topic.title}"? This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(topic.id)}
                              className="bg-destructive text-destructive-foreground"
                              disabled={deleting}
                            >
                              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Delete'}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Editor Dialog */}
      <Dialog open={isEditorOpen} onOpenChange={setIsEditorOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GraduationCap className="h-5 w-5 text-purple-400" />
              {editingTopic ? 'Edit Grammar Topic' : 'New Grammar Topic'}
            </DialogTitle>
            <DialogDescription>
              Fill in the details below to {editingTopic ? 'update' : 'create'} the grammar lesson.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2 md:col-span-2">
                <label className="text-sm font-medium">Title *</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Present Tense Conjugation"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Language *</label>
                <Select 
                  value={formData.language} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, language: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {languages.map(lang => (
                      <SelectItem key={lang.code} value={lang.code}>
                        <span className="flex items-center gap-2">
                          <img 
                            src={`https://flagcdn.com/w20/${lang.flag}.png`} 
                            alt="" 
                            className="w-4 h-3 rounded-sm"
                          />
                          {lang.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Level *</label>
                <Select 
                  value={formData.level} 
                  onValueChange={(v) => {
                    const level = levels.find(l => l.value === v);
                    setFormData(prev => ({ 
                      ...prev, 
                      level: v,
                      cefr_level: level?.cefr || 'A1'
                    }));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {levels.map(level => (
                      <SelectItem key={level.value} value={level.value}>
                        {level.label} ({level.cefr})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Study Time (minutes)</label>
                <Input
                  type="number"
                  min="5"
                  max="120"
                  value={formData.study_time_minutes}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    study_time_minutes: parseInt(e.target.value) || 15 
                  }))}
                />
              </div>
            </div>

            {/* Objectives */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Learning Objectives</label>
              <Textarea
                value={formData.objectives}
                onChange={(e) => setFormData(prev => ({ ...prev, objectives: e.target.value }))}
                placeholder="What will learners be able to do after this lesson?"
                className="min-h-[80px]"
              />
            </div>

            {/* Content */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Content *</label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                placeholder="The main lesson content. Use markdown for formatting..."
                className="min-h-[200px] font-mono text-sm"
              />
            </div>

            {/* Exercises */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Exercises ({formData.exercises.length})</label>
              </div>

              {/* Existing Exercises */}
              {formData.exercises.length > 0 && (
                <div className="space-y-2">
                  {formData.exercises.map((ex, idx) => (
                    <div key={idx} className="flex items-start gap-2 p-3 bg-slate-800/50 rounded-lg">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white">{ex.question}</p>
                        {ex.answer && (
                          <p className="text-xs text-muted-foreground mt-1">Answer: {ex.answer}</p>
                        )}
                        <Badge variant="outline" className="mt-1 text-xs">{ex.type}</Badge>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => removeExercise(idx)}
                        className="h-8 w-8 text-destructive"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add Exercise Form */}
              <Card className="bg-slate-800/30 border-dashed">
                <CardContent className="p-4 space-y-3">
                  <p className="text-sm font-medium text-muted-foreground">Add New Exercise</p>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div className="md:col-span-2">
                      <Input
                        value={currentExercise.question}
                        onChange={(e) => setCurrentExercise(prev => ({ ...prev, question: e.target.value }))}
                        placeholder="Question or prompt"
                      />
                    </div>
                    <div>
                      <Input
                        value={currentExercise.answer}
                        onChange={(e) => setCurrentExercise(prev => ({ ...prev, answer: e.target.value }))}
                        placeholder="Answer (optional)"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Select 
                        value={currentExercise.type} 
                        onValueChange={(v) => setCurrentExercise(prev => ({ ...prev, type: v }))}
                      >
                        <SelectTrigger className="flex-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {exerciseTypes.map(type => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button onClick={addExercise} size="icon">
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditorOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="gap-2">
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {editingTopic ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminGrammarEditor;
